/**
 * Ensure TikTok Thumbnails Cached
 * 
 * Backfills thumbnails for legacy Featured Posts that are missing thumbnailUrl.
 * Rate limited to 3 unfurls per call to avoid overwhelming the API.
 */

import { unfurlTikTokThumbnail } from './unfurlTikTokClient';
import { isLikelyTikTokUrl } from './unfurlTikTokClient';
import type { FeaturedPost } from './normalizeFeaturedPosts';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

const MAX_UNFURLS_PER_CALL = 3;

/**
 * Backfill thumbnails for posts missing thumbnailUrl
 * 
 * @param creatorId - User ID (Firestore document ID)
 * @param posts - Array of normalized Featured Posts
 * @returns Promise that resolves when backfill is complete (or rate limited)
 */
export async function ensureTikTokThumbnailsCached(
  creatorId: string,
  posts: FeaturedPost[]
): Promise<void> {
  if (!creatorId || !posts || posts.length === 0) {
    return;
  }

      // Find posts that need thumbnails (TikTok URLs without thumbnailUrl)
      // Support both url (normalized) and videoUrl/contentUrl (legacy)
      const postsNeedingThumbnails = posts.filter(
        (post) => {
          const postUrl = post.url || (post as any).videoUrl || (post as any).contentUrl;
          return (
            post.platform === 'tiktok' &&
            postUrl &&
            isLikelyTikTokUrl(postUrl) &&
            !post.thumbnailUrl
          );
        }
      );

  if (postsNeedingThumbnails.length === 0) {
    return; // All posts already have thumbnails
  }

  // Rate limit: only process first 3
  const postsToProcess = postsNeedingThumbnails.slice(0, MAX_UNFURLS_PER_CALL);

  // Process each post
  const updates: Array<{ index: number; thumbnailUrl?: string; title?: string }> = [];

  for (const post of postsToProcess) {
    try {
      // Support both url (normalized) and videoUrl/contentUrl (legacy)
      const postUrl = post.url || (post as any).videoUrl || (post as any).contentUrl;
      if (!postUrl) continue;
      
      const result = await unfurlTikTokThumbnail(postUrl);
      
      if (result.thumbnailUrl) {
        updates.push({
          index: posts.indexOf(post),
          thumbnailUrl: result.thumbnailUrl,
          title: result.title,
        });
      }
    } catch (error) {
      console.warn('[ensureTikTokThumbnailsCached] Failed to unfurl thumbnail for post:', post.id, error);
      // Continue with other posts even if one fails
    }
  }

  // If we have updates, write them back to Firestore
  if (updates.length > 0) {
    try {
      const influencerRef = doc(db, 'influencers', creatorId);
      
      // Get current featuredPosts from Firestore to merge updates
      // We'll update the specific posts that got thumbnails
      const updatedPosts = [...posts];
      
      for (const update of updates) {
        const post = updatedPosts[update.index];
        if (post) {
          const postUrl = post.url || (post as any).videoUrl || (post as any).contentUrl;
          updatedPosts[update.index] = {
            ...post,
            url: postUrl, // Ensure url is set
            videoUrl: postUrl, // Alias for backward compatibility
            contentUrl: postUrl, // Alias for backward compatibility
            thumbnailUrl: update.thumbnailUrl,
            title: update.title || post.title,
            thumbnailUpdatedAt: Date.now(),
          };
        }
      }

      // Write back to Firestore
      await updateDoc(influencerRef, {
        featuredPosts: updatedPosts,
      });

      console.log(`[ensureTikTokThumbnailsCached] Backfilled ${updates.length} thumbnail(s) for user ${creatorId}`);
    } catch (error) {
      console.error('[ensureTikTokThumbnailsCached] Failed to update Firestore:', error);
      // Don't throw - this is a background operation
    }
  }
}

